import { LOCALE_TAG_MAX, LOCALE_TAG_PATTERN, PARTNER_ATTRIBUTES_MAX_BYTES } from '@today/models'
import { ProductCtaType } from '@today/types'

/**
 * Base + CTA form rules of the product editor (TO-416 product-admin-ui REQ-003,
 * REQ-004). The CTA target rules mirror product-service's (TO-415 product-catalog
 * REQ-001); the server stays authoritative and its 422 codes are still mapped.
 */

export interface BaseFormValues {
	code: string
	name: string
	defaultLocale: string
	categoryId: string
	featured: boolean
	icon: string
	/** Raw JSON text of the partner attributes; empty means `{}`. */
	partnerAttributes: string
	ctaPrimaryType: ProductCtaType | ''
	ctaPrimaryTarget: string
	ctaSecondaryType: ProductCtaType | ''
	ctaSecondaryTarget: string
}

export const EMPTY_BASE_FORM: BaseFormValues = {
	code: '',
	name: '',
	defaultLocale: '',
	categoryId: '',
	featured: false,
	icon: '',
	partnerAttributes: '',
	ctaPrimaryType: '',
	ctaPrimaryTarget: '',
	ctaSecondaryType: '',
	ctaSecondaryTarget: '',
}

export type ParsedAttributes = { ok: true; value: Record<string, unknown> } | { ok: false; error: string }

/** Parse the partner-attributes text: a JSON object of at most 16384 UTF-8 bytes. */
export const parsePartnerAttributes = (text: string): ParsedAttributes => {
	if (text.trim() === '') return { ok: true, value: {} }
	let parsed: unknown
	try {
		parsed = JSON.parse(text)
	} catch {
		return { ok: false, error: 'form.errors.attributesNotObject' }
	}
	if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
		return { ok: false, error: 'form.errors.attributesNotObject' }
	}
	if (new TextEncoder().encode(JSON.stringify(parsed)).length > PARTNER_ATTRIBUTES_MAX_BYTES) {
		return { ok: false, error: 'form.errors.attributesTooLarge' }
	}
	return { ok: true, value: parsed as Record<string, unknown> }
}

const CTA_TARGET_MAX = 2048

const isHttpsUrl = (target: string): boolean => {
	if (!target.startsWith('https://')) return false
	try {
		return new URL(target).protocol === 'https:' && target.length > 'https://'.length
	} catch {
		return false
	}
}

const CTA_RULES: Record<ProductCtaType, (target: string) => boolean> = {
	[ProductCtaType.EXTERNAL_LINK]: isHttpsUrl,
	[ProductCtaType.PHONE]: (target) => /^\+[1-9][0-9]{6,14}$/.test(target),
	[ProductCtaType.SCHEDULE_MEETING]: () => true,
	[ProductCtaType.IN_APP_ACTION]: (target) => /^[A-Z][A-Z0-9_]{0,63}$/.test(target),
}

/** The CTA target's error key, or null. A CTA of type none has no target to check. */
export const checkCta = (type: ProductCtaType | '', target: string): string | null => {
	if (type === '') return null
	if (target === '') return 'cta.errors.required'
	if (target.length > CTA_TARGET_MAX || !CTA_RULES[type](target)) return `cta.errors.${type}`
	return null
}

export const isLocaleTag = (value: string): boolean => value.length <= LOCALE_TAG_MAX && LOCALE_TAG_PATTERN.test(value)

const lengthError = (value: string, max: number, key: string): string | null => {
	if (value.trim() === '') return 'form.errors.required'
	return value.length > max ? key : null
}

/** Client-side errors keyed by field; empty when the form may be sent. The code is locked in edit mode. */
export const baseFormErrors = (values: BaseFormValues, mode: 'create' | 'edit'): Record<string, string> => {
	const localeError = values.defaultLocale === '' ? 'form.errors.required' : null
	const checks: Record<string, string | null> = {
		code: mode === 'create' ? lengthError(values.code, 64, 'form.errors.codeLength') : null,
		name: lengthError(values.name, 255, 'form.errors.nameLength'),
		defaultLocale: localeError ?? (isLocaleTag(values.defaultLocale) ? null : 'form.errors.localeTag'),
		icon: values.icon.length > 64 ? 'form.errors.iconLength' : null,
		partnerAttributes: (() => {
			const parsed = parsePartnerAttributes(values.partnerAttributes)
			return parsed.ok ? null : parsed.error
		})(),
		ctaPrimaryTarget: checkCta(values.ctaPrimaryType, values.ctaPrimaryTarget),
		ctaSecondaryTarget: checkCta(values.ctaSecondaryType, values.ctaSecondaryTarget),
	}
	return Object.fromEntries(Object.entries(checks).filter((entry): entry is [string, string] => entry[1] !== null))
}

const cta = (type: ProductCtaType | '', target: string) =>
	type === '' ? { type: null, target: null } : { type, target }

/** Every editable field except `code` — the PATCH body (never carries status/origin/tenantId). */
export const toPatchBody = (values: BaseFormValues): Record<string, unknown> => {
	const primary = cta(values.ctaPrimaryType, values.ctaPrimaryTarget)
	const secondary = cta(values.ctaSecondaryType, values.ctaSecondaryTarget)
	const attributes = parsePartnerAttributes(values.partnerAttributes)
	return {
		name: values.name,
		defaultLocale: values.defaultLocale,
		categoryId: values.categoryId === '' ? null : values.categoryId,
		featured: values.featured,
		icon: values.icon === '' ? null : values.icon,
		partnerAttributes: attributes.ok ? attributes.value : {},
		ctaPrimaryType: primary.type,
		ctaPrimaryTarget: primary.target,
		ctaSecondaryType: secondary.type,
		ctaSecondaryTarget: secondary.target,
	}
}

/** The POST body: the code plus every editable field. */
export const toCreateBody = (values: BaseFormValues): Record<string, unknown> => ({
	code: values.code,
	...toPatchBody(values),
})
